import type { FieldDescriptor, ViewerMode } from './signingTypes';
import FieldMarker from './FieldMarker';
import { colorForSigner } from '../../lib/signerColors';

interface FieldOverlayProps {
  fields: FieldDescriptor[];
  activeFieldId: string | null;
  inspectedFieldId?: string | null;
  mode: ViewerMode;
  signaturePreview?: string | null;
  documentId?: string;
  onFieldClick?: (fieldId: string) => void;
  onFieldInspect?: (field: FieldDescriptor) => void;
  onFieldMove?: (fieldId: string, rect: { x: number; y: number; width: number; height: number }) => void;
  /** v1 placement-redesign: per-field × delete + drag-to-confirm callbacks.
   *  Omit in sign mode to keep markers immutable for the recipient. */
  onRequestDelete?: (fieldId: string) => void;
  onConfirmField?: (fieldId: string) => void;
  /** v1 placement-redesign: when true, FieldMarker uses the per-signer
   *  color palette (signerIndex → palette slot). When false (sign mode),
   *  markers fall back to the legacy unified palette. */
  useSignerPalette?: boolean;
}

/**
 * Absolutely positioned overlay matching the page canvas dimensions.
 * Renders FieldMarker for each field on this page using percent-based coords.
 */
export default function FieldOverlay({
  fields, activeFieldId, inspectedFieldId, mode, signaturePreview, documentId,
  onFieldClick, onFieldInspect, onFieldMove, onRequestDelete, onConfirmField,
  useSignerPalette,
}: FieldOverlayProps) {
  if (!fields.length) return null;

  const paletteIndexForField = (field: FieldDescriptor): number => {
    const raw =
      field.signerIndex != null && field.signerIndex >= 0
        ? field.signerIndex
        : field.isRecipientParty
          ? 1
          : 0;
    // Never paint recipient markers with owner slot (green-only semantics).
    if (field.isRecipientParty && raw === 0) return 1;
    return raw;
  };

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
      {fields.map((field) => (
        <FieldMarker
          key={field.id}
          field={field}
          isActive={field.id === activeFieldId}
          isInspected={field.id === inspectedFieldId}
          mode={mode}
          signaturePreview={field.id === activeFieldId || field.signed ? signaturePreview : null}
          documentId={documentId}
          onClick={onFieldClick ? () => onFieldClick(field.id) : undefined}
          onInspect={onFieldInspect}
          onFieldMove={onFieldMove}
          onRequestDelete={onRequestDelete}
          onConfirmField={onConfirmField}
          signerColor={useSignerPalette ? colorForSigner(paletteIndexForField(field)) : undefined}
        />
      ))}
    </div>
  );
}
